import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Users, UserCheck, CalendarCheck, CheckCircle2, CalendarClock, TrendingUp,
  Wallet, ArrowUpRight, ClipboardCheck, Activity,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { StatCard, PageHeader, UserAvatar, EmptyState, Spinner, SectionCard } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { fmtMoney } from "@/lib/constants";

function ChartTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card shadow-float px-3 py-2">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold tabular">{payload[0].value} {t("hr.dashboard.sessionsUnit")}</p>
    </div>
  );
}

export default function HrDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);

  useEffect(() => {
    if (user?.company && user.company.onboarded === false) { navigate("/onboarding"); return; }
    api.get("/company/dashboard").then(({ data }) => setD(data)).catch(() => {});
  }, [user, navigate]);

  if (!d) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;

  const companyName = user?.company?.name || t("hr.dashboard.yourWorkspace");
  const budgetPct = d.budget_total ? Math.min(100, Math.round((d.budget_used / d.budget_total) * 100)) : 0;
  const cards = [
    { icon: Users, label: t("hr.dashboard.employees"), value: d.employees, sub: t("hr.dashboard.employeesSub"), accent: true },
    { icon: UserCheck, label: t("hr.dashboard.networkExperts"), value: d.experts, sub: t("hr.dashboard.networkExpertsSub") },
    { icon: CalendarCheck, label: t("hr.dashboard.sessionsBooked"), value: d.booked_sessions, sub: t("hr.dashboard.sessionsBookedSub") },
    { icon: CheckCircle2, label: t("hr.dashboard.completed"), value: d.completed_sessions, sub: t("hr.dashboard.completedSub") },
  ];

  return (
    <AppShell>
      <PageHeader eyebrow={user?.company?.name || t("hr.eyebrow")} title={t("hr.dashboard.welcome", { name: user?.name?.split(" ")[0] })} description={t("hr.dashboard.subtitle", { company: companyName })}>
        <Link to="/hr/employees"><Button data-testid="invite-cta" className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><Users size={16} /> {t("hr.dashboard.invite")}</Button></Link>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        {cards.map((c, i) => <div key={c.label} className={`animate-fade-up d${i + 1}`}><StatCard {...c} /></div>)}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* Trend chart */}
        <SectionCard
          icon={Activity}
          title={t("hr.dashboard.activity")}
          description={t("hr.dashboard.last6Weeks")}
          className="lg:col-span-2 animate-fade-up d2"
          action={<span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary"><TrendingUp size={16} /> {t("hr.dashboard.adoption", { rate: d.adoption_rate })}</span>}
        >
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trend} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id="fillPrimary" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(232 80% 55%)" stopOpacity={0.22} />
                    <stop offset="100%" stopColor="hsl(232 80% 55%)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "hsl(240 5% 45%)" }} />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "hsl(240 6% 90%)" }} />
                <Area type="monotone" dataKey="sessions" stroke="hsl(232 80% 55%)" strokeWidth={2.5} fill="url(#fillPrimary)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </SectionCard>

        {/* Budget + upcoming */}
        <div className="space-y-6">
          <SectionCard icon={Wallet} title={t("hr.dashboard.budget")} description={t("hr.dashboard.budgetSub")} className="animate-fade-up d3">
            <p className="text-2xl font-semibold tabular">{fmtMoney(d.budget_used, d.currency)} <span className="text-sm font-normal text-muted-foreground">{t("hr.dashboard.budgetOf", { total: fmtMoney(d.budget_total, d.currency) })}</span></p>
            <div className="mt-3 h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${budgetPct}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-2">{t("hr.dashboard.budgetUsed", { pct: budgetPct, count: d.booked_sessions })}</p>
            <Link to="/hr/rates" data-testid="dashboard-manage-rates" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline mt-3">
              {t("rates.manageLink")} <ArrowUpRight size={13} />
            </Link>
          </SectionCard>
          <StatCard icon={CalendarClock} label={t("hr.dashboard.upcoming")} value={d.upcoming_sessions} sub={t("hr.dashboard.upcomingSub")} className="animate-fade-up d4" />
        </div>
      </div>

      {/* Most active experts */}
      <SectionCard icon={UserCheck} title={t("hr.dashboard.mostActive")} description={t("hr.dashboard.mostActiveSub")} className="mt-6 animate-fade-up d4">
        {d.most_active?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {d.most_active.map((e, i) => (
              <div key={e.expert_id || e.name || i} className="flex items-center gap-3 rounded-xl border border-border p-3.5 transition-colors hover:border-primary/30 hover:bg-secondary/30">
                <UserAvatar src={fileUrl(e.picture)} name={e.name} size={40} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{e.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{e.title}</p>
                </div>
                <span className="text-sm font-semibold tabular text-primary">{e.sessions}</span>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState icon={ClipboardCheck} title={t("hr.dashboard.noSessions")} description={t("hr.dashboard.noSessionsDesc")}
            action={<Link to="/hr/network"><Button variant="outline" className="rounded-lg gap-1.5">{t("hr.dashboard.manageNetwork")} <ArrowUpRight size={15} /></Button></Link>} />
        )}
      </SectionCard>
    </AppShell>
  );
}
